// ─── AutoChinBot Monitor ───────────────────────────────────────
// Central control section for the AutoChinBot. It contains ONE main table
// loaded from the selected symbol list: the leftmost column lists the
// symbols and the other functional columns will be designed in later phases.
//
// Phase 1: connect to cTrader (streaming price), remember the session, run in
// the background, and show the live control table.

import type { Strategy } from '@/types/strategy';
import { useAutoChinMonitorLogic } from './useAutoChinMonitorLogic';
import { AUTO_CHIN_TIMEFRAME, cn, type AutoChinCtraderAccount } from './auto-chin-monitor/types';
import AutoChinSymbolTable from './auto-chin-monitor/AutoChinSymbolTable';
import AutoChinPendingOrders from './auto-chin-monitor/AutoChinPendingOrders';
import AutoChinTelegramPanel from './auto-chin-monitor/AutoChinTelegramPanel';

interface AutoChinMonitorProps {
  strategy: Strategy;
  ctraderAccounts?: AutoChinCtraderAccount[];
  isActive: boolean;
  initialAccountId?: string;
}

export default function AutoChinMonitor({
  strategy,
  ctraderAccounts = [],
  isActive,
  initialAccountId,
}: AutoChinMonitorProps) {
  const m = useAutoChinMonitorLogic({ strategy, ctraderAccounts, isActive, initialAccountId });

  return (
    <div className="space-y-4">
      {/* ─── Top Bar ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Symbol list dropdown */}
          <div className="relative" ref={m.dropdownRef}>
            <button
              type="button"
              onClick={() => m.setDropdownOpen(!m.dropdownOpen)}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 border rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap min-w-[220px]',
                m.selectedList
                  ? 'bg-background-50 border-background-300/60 hover:border-background-400/60 text-foreground-900'
                  : 'bg-primary-500 border-primary-500 text-background-50 hover:bg-primary-600',
                m.dropdownOpen && 'ring-2 ring-primary-500/20 border-primary-500/50'
              )}
            >
              <i className={cn('text-lg', m.selectedList ? 'ri-list-check-3 text-foreground-500' : 'ri-add-line')}></i>
              <div className="flex-1 text-left">
                {m.selectedList ? (
                  <>
                    <span className="font-semibold block">{m.selectedList.name}</span>
                    <span className="text-[10px] opacity-60 block">{m.selectedList.symbols.length} symbols</span>
                  </>
                ) : (
                  <span className="font-semibold">Select Symbol List</span>
                )}
              </div>
              <i className={cn('text-sm', m.dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line', m.selectedList ? 'text-foreground-400' : 'text-background-50/70')}></i>
            </button>

            {m.dropdownOpen && (
              <div className="absolute top-full mt-1.5 left-0 w-full bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-30 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {m.loadingLists ? (
                    <div className="px-4 py-6 text-center">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-xs text-foreground-400">Loading lists...</p>
                    </div>
                  ) : m.symbolLists.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <i className="ri-emotion-sad-line text-foreground-300 text-xl block mb-2"></i>
                      <p className="text-xs text-foreground-400">No symbol lists created yet</p>
                    </div>
                  ) : (
                    m.symbolLists.map((list) => (
                      <button
                        key={list.id}
                        type="button"
                        onClick={() => m.handleSelectList(list)}
                        className={cn(
                          'w-full text-left px-4 py-3 flex items-center gap-3 transition-colors cursor-pointer',
                          m.selectedListId === list.id ? 'bg-primary-500/10 text-primary-600' : 'hover:bg-background-100 text-foreground-700'
                        )}
                      >
                        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', m.selectedListId === list.id ? 'bg-primary-500/20' : 'bg-background-200/70')}>
                          <i className={cn('text-sm', m.selectedListId === list.id ? 'ri-check-line text-primary-500' : 'ri-list-check-2 text-foreground-400')}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium block truncate">{list.name}</span>
                          <span className="text-[10px] text-foreground-400">{list.symbols.length} symbols</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Account */}
          {m.currentAccount && m.selectedAccountId && (
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg">
              <div className={cn('w-2 h-2 rounded-full shrink-0', m.connected ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300')}></div>
              <span className="text-[10px] text-foreground-500">Account:</span>
              <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">{m.currentAccount.account_name}</span>
              <span className="text-[10px] text-foreground-400">#{m.selectedAccountId}</span>
            </div>
          )}

          {/* Timeframe */}
          {m.selectedList && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500/10 text-accent-500 rounded-full text-[10px] font-medium">
              <i className="ri-time-line"></i>
              {AUTO_CHIN_TIMEFRAME}
            </span>
          )}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {m.selectedList && (
            <button
              type="button"
              onClick={m.isEngineRunning ? m.stopEngine : m.startEngine}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all cursor-pointer whitespace-nowrap',
                m.isEngineRunning
                  ? 'bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20'
                  : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/20'
              )}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={cn('text-[12px]', m.isEngineRunning ? 'ri-stop-circle-line' : 'ri-play-circle-line')}></i>
              </div>
              <span>{m.isEngineRunning ? 'Stop Engine' : 'Start Engine'}</span>
            </button>
          )}

          {m.isEngineRunning && m.connected && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-[10px] font-medium">
              <i className="ri-flashlight-line"></i>
              Live
            </span>
          )}
          {m.isLoading && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 text-amber-400 rounded-full text-[10px] font-medium">
              <div className="w-2.5 h-2.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
              Loading data...
            </span>
          )}
          {m.engineState && m.engineState.symbolCount > 0 && (
            <span className="text-[10px] text-foreground-400">
              {m.engineState.loadedCount}/{m.engineState.symbolCount} loaded
            </span>
          )}
          {!m.selectedList && !m.isLoading && (
            <span className="text-[10px] text-foreground-400">Select a symbol list to begin</span>
          )}
        </div>
      </div>

      {/* ─── Telegram 2-way engine ─── */}
      <AutoChinTelegramPanel />

      {/* ─── Error Banner ─── */}
      {m.errorMsg && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-400 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-heading font-semibold text-red-400 mb-1">Data Load Error</h4>
            <p className="text-xs text-foreground-600 break-words">{m.errorMsg}</p>
          </div>
          <button
            type="button"
            onClick={m.startEngine}
            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-refresh-line mr-1"></i>
            Retry
          </button>
        </div>
      )}

      {/* ─── Loading ─── */}
      {m.isLoading && m.selectedList && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-4">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Loading {AUTO_CHIN_TIMEFRAME} Market Data</h4>
          <p className="text-xs text-foreground-500 max-w-sm mx-auto">
            Fetching historical {AUTO_CHIN_TIMEFRAME} candles for {m.selectedList.name} ({m.selectedList.symbols.length} symbols)...
          </p>
        </div>
      )}

      {/* ─── Restoring workspace ─── */}
      {!m.selectedList && !m.isLoading && m.workspaceLoading && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-5">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">Restoring Workspace</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">Loading your saved AutoChinBot workspace from the cloud...</p>
        </div>
      )}

      {/* ─── Empty ─── */}
      {!m.selectedList && !m.isLoading && !m.workspaceLoading && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-5">
            <i className="ri-robot-2-line text-foreground-300 text-3xl"></i>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">AutoChinBot Control Center</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            Choose a symbol list from the dropdown above to start monitoring. The bot streams
            tick-by-tick real-time prices and keeps a {AUTO_CHIN_TIMEFRAME} candle buffer for each symbol.
          </p>
        </div>
      )}

      {/* ─── Main Table ─── */}
      {!m.isLoading && m.selectedList && m.displaySymbols.length > 0 && (
        <AutoChinSymbolTable
          selectedList={m.selectedList}
          symbols={m.displaySymbols}
          engineState={m.engineState}
          symbolConfigs={m.symbolConfigs}
          connected={m.connected}
          onConfigChange={m.handleConfigChange}
        />
      )}

      {/* ─── Pending Orders ("Vào lệnh chờ") ─── */}
      {m.selectedList && (
        <AutoChinPendingOrders
          orders={m.pendingOrders}
          paused={m.pendingPaused}
          pauseReason={m.pendingPauseReason}
          onDelete={m.deletePendingOrder}
          onEdit={m.updatePendingOrder}
          onResume={m.resumePendingOrders}
        />
      )}

    </div>
  );
}